import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../config/axios';
import toast from 'react-hot-toast';
import { Check } from 'lucide-react';

const PerformanceSurveyTaker = () => {
  const { id, token } = useParams();
  const [pairInfo, setPairInfo] = useState(null);
  const [valid, setValid] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [evaluationCompleted, setEvaluationCompleted] = useState(false);
  const fileInputRef = useRef();
  const [timeLeft, setTimeLeft] = useState(null);
  const timerRef = useRef();
  const autoSubmittedRef = useRef(false);
  // Flag efímero para distinguir cómo se completó (solo para decidir overlay al cargar si ya está completada)
  const completionKey = `perf-eval-completed-${id}-${token}`;

  // Helper para mostrar un overlay modal y cerrar automáticamente si se desea
  const showOverlay = useCallback((icon, title, message) => {
    const overlay = document.createElement('div');
    overlay.style.position = 'fixed';
    overlay.style.inset = '0';
    overlay.style.background = '#eefdfa';
    overlay.style.display = 'flex';
    overlay.style.alignItems = 'center';
    overlay.style.justifyContent = 'center';
    overlay.style.zIndex = '99999';
    const box = document.createElement('div');
    box.style.background = '#fff';
    box.style.border = '2px solid #19B394';
    box.style.borderRadius = '18px';
    box.style.boxShadow = '0 4px 24px rgba(25,179,148,0.10)';
    box.style.padding = '40px 32px';
    box.style.textAlign = 'center';
    const safeIcon = icon || '✅';
    const safeTitle = title || 'Operación realizada';
    const safeMsg = message || '';
    box.innerHTML = `<div style="font-size:54px; margin-bottom:8px">${safeIcon}</div><div style="font-size:28px; color:#19B394; font-weight:700; margin-bottom:8px">${safeTitle}</div><div style="color:#555">${safeMsg}</div>`;
    overlay.appendChild(box);
    document.body.appendChild(overlay);
    return overlay;
  }, []);

  // Autoenviar cuando se agota el tiempo, con lo que haya respondido (definido antes de efectos que lo usan)
  const handleAutoSubmit = useCallback(async () => {
    if (autoSubmittedRef.current || evaluationCompleted) return;
    autoSubmittedRef.current = true;
    try {
      const allAnswers = [];
      (Array.isArray(survey?.pages) && survey.pages.length ? survey.pages : [survey?.questions]).forEach((pg, pgIdx) => {
        const qs = Array.isArray(pg) ? pg : (pg && Array.isArray(pg.questions) ? pg.questions : []);
        qs.forEach((q, qIdx) => {
          let ans = answers[pgIdx]?.[qIdx];
          if (q.type === 'slider' && Array.isArray(q.sliders)) {
            ans = Array.isArray(ans) ? ans : [];
            ans = q.sliders.map((slider, sIdx) => ans[sIdx] !== undefined ? ans[sIdx] : slider.min);
          }
          allAnswers.push({ questionId: q.id, answer: ans ?? '' });
        });
      });
      await api.post(`/api/performance-evaluation/forms/${id}/responses`, {
        answers: allAnswers,
        respondentInfo: { submittedAt: new Date().toISOString(), autoSubmitted: true, evaluator: pairInfo?.evaluator?.dni, evaluatee: pairInfo?.evaluatee?.dni },
        token
      });
      await api.post('/api/performance-evaluation/submit', { surveyId: id, token });
      setEvaluationCompleted(true);
      // Marcar razón y mostrar overlay de tiempo agotado y cerrar
      try { sessionStorage.setItem(completionKey, 'timeout'); } catch { }
      showOverlay('⏰', 'Tiempo agotado', 'Tus respuestas fueron enviadas automáticamente. Esta pestaña se cerrará automáticamente.');
      setTimeout(() => {
        window.close();
      }, 1500);
      try {
        if ('BroadcastChannel' in window) {
          const bc = new BroadcastChannel('evaluation-updates');
          bc.postMessage({ type: 'updated' });
          bc.close();
        }
        localStorage.setItem('evaluation-updated', String(Date.now()));
      } catch { }
    } catch (e) {
      toast.error('No se pudo enviar automáticamente la evaluación');
    }
  }, [answers, evaluationCompleted, id, pairInfo?.evaluator?.dni, pairInfo?.evaluatee?.dni, survey, token, showOverlay, completionKey]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/api/performance-evaluation/public/evaluatee/${id}/${token}`);
        if (!res.data.valid) {
          setValid(false);
          return;
        }
        setPairInfo(res.data);
        setValid(true);

        // Bloquear reingreso si ya está completada
        const pairStatus = res.data.pairStatus || res.data.status;
        const submittedAt = res.data.submittedAt;
        if (pairStatus === 'completed') {
          setEvaluationCompleted(true);
          let reason = null;
          try { reason = sessionStorage.getItem(completionKey); } catch { }
          if (reason === 'manual') {
            showOverlay('✅', '¡Evaluación enviada!', 'Tu participación ha sido registrada exitosamente. Esta pestaña se cerrará automáticamente.');
          } else if (reason === 'timeout') {
            showOverlay('⏰', 'Tiempo agotado', 'Tus respuestas fueron enviadas automáticamente. Esta pestaña se cerrará automáticamente.');
          } else {
            const when = submittedAt ? new Date(submittedAt).toLocaleString() : null;
            showOverlay('✅', 'Evaluación ya respondida', when ? `Esta evaluación fue enviada el ${when}. Esta pestaña se cerrará automáticamente.` : 'Esta evaluación ya fue respondida. Esta pestaña se cerrará automáticamente.');
          }
          try { sessionStorage.removeItem(completionKey); } catch { }
          setTimeout(() => {
            window.close();
          }, 1200);
          return; // No continuar cargando ni marcar en progreso
        }

        // Marcar como en progreso inmediatamente
        await api.post('/api/performance-evaluation/mark-in-progress', { surveyId: id, token });

        // Notificar al dashboard inmediatamente para actualizar el estado
        try {
          if ('BroadcastChannel' in window) {
            const bc = new BroadcastChannel('evaluation-updates');
            bc.postMessage({ type: 'status-changed', status: 'in_progress' });
            bc.close();
          }
          localStorage.setItem('evaluation-updated', String(Date.now()));
        } catch { }

        // Cargar encuesta para desempeño
        const surveyRes = await api.get(`/api/forms/${id}/open`);
        const data = surveyRes.data;
        if (!data.pages && Array.isArray(data.questions)) data.pages = [data.questions];
        if (!data.questions && Array.isArray(data.pages)) {
          data.questions = data.pages.flatMap(pg => Array.isArray(pg)
            ? pg
            : (Array.isArray(pg?.questions) ? pg.questions : []));
        }
        // Inicializar respuestas por página y pregunta
        const pagesArr = Array.isArray(data.pages) && data.pages.length
          ? data.pages
          : [{ questions: Array.isArray(data.questions) ? data.questions : [] }];
        // Asegurar que el estado tenga pages normalizado
        data.pages = pagesArr;
        setSurvey(data);
        const initial = {};
        pagesArr.forEach((pg, pgIdx) => {
          const qs = Array.isArray(pg) ? pg : (Array.isArray(pg.questions) ? pg.questions : []);
          initial[pgIdx] = {};
          qs.forEach((q, qIdx) => {
            if (q.type === 'checkbox') initial[pgIdx][qIdx] = [];
            else if (q.type === 'scale') initial[pgIdx][qIdx] = null;
            else if (q.type === 'slider' && Array.isArray(q.sliders)) initial[pgIdx][qIdx] = q.sliders.map(s => s.min ?? 0);
            else initial[pgIdx][qIdx] = '';
          });
        });
        setAnswers(initial);
        setCurrentPage(0);
      } catch (e) {
        // Fallback: permitir continuar para evitar pantalla en blanco y cargar el formulario
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Validación de acceso fallida (transitoria):', e?.message || e);
        }
        setValid(true);
        try {
          const surveyRes = await api.get(`/api/forms/${id}/open`);
          const data = surveyRes.data;
          if (!data.pages && Array.isArray(data.questions)) data.pages = [data.questions];
          if (!data.questions && Array.isArray(data.pages)) {
            data.questions = data.pages.flatMap(pg => Array.isArray(pg)
              ? pg
              : (Array.isArray(pg?.questions) ? pg.questions : []));
          }
          // Inicializar respuestas por página y pregunta (fallback)
          const pagesArr = Array.isArray(data.pages) && data.pages.length
            ? data.pages
            : [{ questions: Array.isArray(data.questions) ? data.questions : [] }];
          // Asegurar que el estado tenga pages normalizado
          data.pages = pagesArr;
          setSurvey(data);
          const initial = {};
          pagesArr.forEach((pg, pgIdx) => {
            const qs = Array.isArray(pg) ? pg : (Array.isArray(pg.questions) ? pg.questions : []);
            initial[pgIdx] = {};
            qs.forEach((q, qIdx) => {
              if (q.type === 'checkbox') initial[pgIdx][qIdx] = [];
              else if (q.type === 'scale') initial[pgIdx][qIdx] = null;
              else if (q.type === 'slider' && Array.isArray(q.sliders)) initial[pgIdx][qIdx] = q.sliders.map(s => s.min ?? 0);
              else initial[pgIdx][qIdx] = '';
            });
          });
          setAnswers(initial);
          setCurrentPage(0);
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('Carga de formulario fallida en fallback:', err?.message || err);
          }
        }
      } finally {
        setLoading(false);
      }
    };
    load();

    // Actualizar actividad cada 30 segundos para mantener la evaluación activa (más frecuente)
    const activityInterval = setInterval(async () => {
      if (!evaluationCompleted) {
        try {
          await api.post('/api/performance-evaluation/update-activity', { surveyId: id, token });
        } catch (error) {
          console.error('Error actualizando actividad:', error);
        }
      }
    }, 30 * 1000); // 30 segundos

    // Efecto para textareas automáticas (se aplicará en otro useEffect específico)

    // Manejador para cerrar pestaña sin envío - más agresivo
    const handleBeforeUnload = (e) => {
      if (!evaluationCompleted) {
        // Usar sendBeacon para mayor confiabilidad
        const data = JSON.stringify({ surveyId: id, token });
        if ('sendBeacon' in navigator) {
          navigator.sendBeacon('/api/performance-evaluation/revert-to-pending', data);
        } else {
          // Fallback: hacer la llamada de manera síncrona
          const xhr = new XMLHttpRequest();
          xhr.open('POST', '/api/performance-evaluation/revert-to-pending', false);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.send(data);
        }

        // Notificar al dashboard inmediatamente
        try {
          if ('BroadcastChannel' in window) {
            const bc = new BroadcastChannel('evaluation-updates');
            bc.postMessage({ type: 'status-changed', status: 'pending' });
            bc.close();
          }
          localStorage.setItem('evaluation-updated', String(Date.now()));
        } catch { }
      }
    };

    // Manejador para cierre de pestaña - más agresivo
    const handlePageHide = () => {
      if (!evaluationCompleted) {
        // Usar sendBeacon para mayor confiabilidad
        const data = JSON.stringify({ surveyId: id, token });
        if ('sendBeacon' in navigator) {
          navigator.sendBeacon('/api/performance-evaluation/revert-to-pending', data);
        } else {
          // Fallback: hacer la llamada de manera síncrona
          const xhr = new XMLHttpRequest();
          xhr.open('POST', '/api/performance-evaluation/revert-to-pending', false);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.send(data);
        }

        // Notificar al dashboard inmediatamente
        try {
          if ('BroadcastChannel' in window) {
            const bc = new BroadcastChannel('evaluation-updates');
            bc.postMessage({ type: 'status-changed', status: 'pending' });
            bc.close();
          }
          localStorage.setItem('evaluation-updated', String(Date.now()));
        } catch { }
      }
    };

    // Manejador para pérdida de foco de ventana - más agresivo
    const handleVisibilityChange = () => {
      if (document.hidden && !evaluationCompleted) {
        // Usar sendBeacon para mayor confiabilidad
        const data = JSON.stringify({ surveyId: id, token });
        if ('sendBeacon' in navigator) {
          navigator.sendBeacon('/api/performance-evaluation/revert-to-pending', data);
        } else {
          // Fallback: hacer la llamada de manera síncrona
          const xhr = new XMLHttpRequest();
          xhr.open('POST', '/api/performance-evaluation/revert-to-pending', false);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.send(data);
        }

        // Notificar al dashboard inmediatamente
        try {
          if ('BroadcastChannel' in window) {
            const bc = new BroadcastChannel('evaluation-updates');
            bc.postMessage({ type: 'status-changed', status: 'pending' });
            bc.close();
          }
          localStorage.setItem('evaluation-updated', String(Date.now()));
        } catch { }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(activityInterval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [id, token, evaluationCompleted, showOverlay, completionKey]);

  // Iniciar temporizador si la evaluación tiene timeLimit (HH:MM:SS)
  useEffect(() => {
    if (!survey || !survey.timeLimit || evaluationCompleted) return;
    const tl = survey.timeLimit;
    let totalSeconds = 0;
    if (typeof tl === 'number') {
      // Interpretar como minutos
      totalSeconds = Math.max(0, Math.floor(tl) * 60);
    } else {
      const parts = String(tl).split(':').map(n => Number(n));
      if (parts.length === 3) {
        const [h, m, s] = parts;
        totalSeconds = (h || 0) * 3600 + (m || 0) * 60 + (s || 0);
      } else if (parts.length === 2) {
        const [m, s] = parts;
        totalSeconds = (m || 0) * 60 + (s || 0);
      } else if (parts.length === 1 && !isNaN(parts[0])) {
        // Interpretar como minutos
        totalSeconds = (parts[0] || 0) * 60;
      }
    }
    setTimeLeft(totalSeconds);
  }, [survey, evaluationCompleted]);

  // Cuenta regresiva y autoenvío al llegar a 0
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0 || evaluationCompleted) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev === 1) {
          clearInterval(timerRef.current);
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [timeLeft, evaluationCompleted, handleAutoSubmit]);

  const formatTimeFull = (seconds) => {
    const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
    const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
    const s = String(seconds % 60).padStart(2, '0');
    return { h, m, s };
  };



  // Efecto para textareas automáticas cuando cambie la página
  useEffect(() => {
    if (survey) {
      setTimeout(() => {
        document.querySelectorAll('.response-input-taker').forEach(textarea => {
          textarea.addEventListener('input', () => {
            textarea.style.height = 'auto';
            textarea.style.height = textarea.scrollHeight + 'px';
          });
          textarea.dispatchEvent(new Event('input'));
        });
      }, 100);
    }
  }, [currentPage, survey]);



  if (valid === false) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f8fa', color: '#dc3545' }}>
        Acceso denegado
      </div>
    );
  }
  if (valid === null) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f8fa', color: '#19B394' }}>
        Cargando evaluación...
      </div>
    );
  }
  const pages = (Array.isArray(survey?.pages) && survey.pages.length) ? survey.pages : [survey?.questions];
  const pageObj = Array.isArray(pages[0]) ? { questions: pages[currentPage] } : (pages[currentPage] || { questions: [] });
  const questions = Array.isArray(pageObj) ? pageObj : (pageObj && Array.isArray(pageObj.questions) ? pageObj.questions : []);

  const handleAnswerChange = (pageIndex, value) => {
    setAnswers(prev => ({ ...prev, [pageIndex]: value }));
  };

  const isQuestionValid = (q, ans) => {
    if (!q.required) return true;
    if (q.type === 'checkbox') return Array.isArray(ans) && ans.length > 0;
    if (q.type === 'scale') return ans !== null && ans !== undefined;
    if (q.type === 'file_upload') return !!ans;
    if (q.type === 'matrix') {
      if (!ans) return false;
      return Array.isArray(q.rows) && q.rows.every((_, rowIdx) => typeof ans[rowIdx] === 'number');
    }
    if (typeof ans === 'string') return ans.trim() !== '';
    return !!ans;
  };

  const buildAllAnswers = () => {
    const allAnswers = [];
    ((Array.isArray(survey.pages) && survey.pages.length) ? survey.pages : [survey.questions]).forEach((pg, pgIdx) => {
      const qs = Array.isArray(pg) ? pg : (pg && Array.isArray(pg.questions) ? pg.questions : []);
      qs.forEach((q, qIdx) => {
        let ans = answers[pgIdx]?.[qIdx];
        if (q.type === 'slider' && Array.isArray(q.sliders)) {
          ans = Array.isArray(ans) ? ans : [];
          ans = q.sliders.map((slider, sIdx) => ans[sIdx] !== undefined ? ans[sIdx] : slider.min);
        }
        allAnswers.push({ questionId: q.id, answer: ans ?? '' });
      });
    });
    return allAnswers;
  };

  const handleSubmit = async () => {
    // Validar todos los requeridos
    let missing = false;
    ((Array.isArray(survey.pages) && survey.pages.length) ? survey.pages : [survey.questions]).forEach((pg, pgIdx) => {
      const qs = Array.isArray(pg) ? pg : (pg && Array.isArray(pg.questions) ? pg.questions : []);
      qs.forEach((q, qIdx) => {
        if (!isQuestionValid(q, answers[pgIdx]?.[qIdx])) missing = true;
      });
    });
    if (missing) {
      toast.error('Por favor responde todas las preguntas obligatorias');
      return;
    }
    setSubmitting(true);
    try {
      const allAnswers = buildAllAnswers();
      await api.post(`/api/performance-evaluation/forms/${id}/responses`, {
        answers: allAnswers,
        respondentInfo: { submittedAt: new Date().toISOString(), evaluator: pairInfo?.evaluator?.dni, evaluatee: pairInfo?.evaluatee?.dni },
        token
      });
      await api.post('/api/performance-evaluation/submit', { surveyId: id, token });

      // Marcar como completada para evitar que se revierta el estado
      setEvaluationCompleted(true);

      // Notificar al dashboard para refrescar inmediatamente
      try {
        if ('BroadcastChannel' in window) {
          const bc = new BroadcastChannel('evaluation-updates');
          bc.postMessage({ type: 'updated' });
          bc.close();
        }
        localStorage.setItem('evaluation-updated', String(Date.now()));
      } catch { }
      // Guardar razón para evitar overlay de "ya respondida" en re-montaje inmediato
      try { sessionStorage.setItem(completionKey, 'manual'); } catch { }
      // Mostrar modal "Encuesta enviada" y cerrar pestaña tras 1s
      const overlay = document.createElement('div');
      overlay.style.position = 'fixed';
      overlay.style.inset = '0';
      overlay.style.background = '#eefdfa';
      overlay.style.display = 'flex';
      overlay.style.alignItems = 'center';
      overlay.style.justifyContent = 'center';
      overlay.style.zIndex = '99999';
      const box = document.createElement('div');
      box.style.background = '#fff';
      box.style.border = '2px solid #19B394';
      box.style.borderRadius = '18px';
      box.style.boxShadow = '0 4px 24px rgba(25,179,148,0.10)';
      box.style.padding = '40px 32px';
      box.style.textAlign = 'center';
      box.innerHTML = '<div style="font-size:54px; margin-bottom:8px">✅</div><div style="font-size:28px; color:#19B394; font-weight:700; margin-bottom:8px">¡Evaluación enviada!</div><div style="color:#555">Tu participación ha sido registrada exitosamente.</div>';
      overlay.appendChild(box);
      document.body.appendChild(overlay);
      setTimeout(() => {
        window.close();
      }, 1000);
    } catch (e) {
      toast.error('No se pudo enviar la evaluación');
    } finally {
      setSubmitting(false);
    }
  };



  // Subir archivo al backend y obtener ruta
  async function uploadFileTobackend(file) {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post('/api/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data.filePath;
  }

  return (
    <div>
      <div className="survey-taker-container" style={{ position: 'relative', minHeight: '100vh' }}>
        {/* Temporizador como en SurveyTaker */}
        {survey?.timeLimit && timeLeft !== null && timeLeft > 0 && (
          <div
            style={{
              position: 'fixed',
              top: 18,
              right: 18,
              zIndex: 999,
              background: '#EDF9F7',
              borderRadius: 10,
              boxShadow: '0 2px 8px #19b39422',
              padding: '10px 18px 6px 18px',
              minWidth: 140,
              textAlign: 'center',
              border: '2px solid #19B394',
              width: 'auto',
              maxWidth: '95vw'
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 13, color: '#19B394', letterSpacing: 1, marginBottom: 3 }}>TEMPORIZADOR</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, color: '#222', letterSpacing: 2, marginBottom: 1 }}>
              {formatTimeFull(timeLeft).h} <span style={{ margin: '0 5px', fontWeight: 400, color: '#19B394' }}> : </span> {formatTimeFull(timeLeft).m} <span style={{ margin: '0 5px', fontWeight: 400, color: '#19B394' }}> : </span> {formatTimeFull(timeLeft).s}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 0 }}>
              <span style={{ fontSize: 9.5, color: '#19B394', fontWeight: 600 }}>HORAS</span>
              <span style={{ fontSize: 9.5, color: '#19B394', fontWeight: 600 }}>MINUTOS</span>
              <span style={{ fontSize: 9.5, color: '#19B394', fontWeight: 600 }}>SEGUNDOS</span>
            </div>
          </div>
        )}
        <div className="survey-taker" style={{ paddingTop: 20 }}>
          {/* Header igual al SurveyTaker */}
          {survey && (
            <div className="survey-header">
              <h1 className="survey-taker-title">{survey.title}</h1>
              {survey.description && (
                <div className="survey-taker-description" style={{ whiteSpace: 'pre-line', textAlign: 'justify' }}>{survey.description}</div>
              )}
            </div>
          )}

          {/* Bloque de datos del evaluador */}
          {pairInfo?.evaluator && (
            <div className="info-section-wrapper">
              <div className="info-card">
                <div className="info-card-header">
                  <h5 className="info-card-title">Datos del Evaluador</h5>
                </div>
                <div className="info-card-content">
                  <div>
                    <span className="info-label">Empresa:</span>
                    <span className="info-value">{pairInfo.evaluator.company || '-'}</span>
                  </div>
                  <div>
                    <span className="info-label">Apellidos y Nombres:</span>
                    <span className="info-value">{pairInfo.evaluator.name || '-'}</span>
                  </div>
                  <div>
                    <span className="info-label">Puesto:</span>
                    <span className="info-value">{pairInfo.evaluator.jobPosition || '-'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Bloque de datos personales del evaluado - PROFESIONAL */}
          {pairInfo?.evaluatee && (
            <div className="info-section-wrapper">
              <div className="info-card">
                <div className="info-card-header">
                  <h5 className="info-card-title">Datos del Evaluado</h5>
                </div>
                <div className="info-card-content">
                  <div>
                    <span className="info-label">Empresa:</span>
                    <span className="info-value">{pairInfo.evaluatee.company || '-'}</span>
                  </div>
                  <div>
                    <span className="info-label">Apellidos y Nombres:</span>
                    <span className="info-value">{pairInfo.evaluatee.name || '-'}</span>
                  </div>
                  <div>
                    <span className="info-label">Área:</span>
                    <span className="info-value">{pairInfo.evaluatee.area || '-'}</span>
                  </div>
                  <div>
                    <span className="info-label">Puesto:</span>
                    <span className="info-value">{pairInfo.evaluatee.jobPosition || '-'}</span>
                  </div>
                  <div>
                    <span className="info-label">Fecha de Ingreso:</span>
                    <span className="info-value">
                      {(() => {
                        const raw = (pairInfo?.evaluatee && (pairInfo.evaluatee.companyEntryDate ?? pairInfo.evaluatee.fechaIngreso)) || null;
                        if (!raw) return '-';
                        const d = new Date(raw);
                        return isNaN(d.getTime()) ? String(raw) : d.toLocaleDateString();
                      })()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Título de página si existe */}
          {pageObj?.pageTitle && (
            <h2 className="survey-taker-page-title" style={{ fontSize: '1.2rem', fontWeight: 600, color: '#222', textAlign: 'left', margin: '20px 0 5px 10px' }}>{pageObj.pageTitle}</h2>
          )}
          {pageObj?.pageDescription && (
            <p className="survey-taker-page-description" style={{ fontSize: '1.08rem', color: '#5a5a5a', textAlign: 'left', margin: '0 0 5px 10px' }}>{pageObj.pageDescription}</p>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#19B394' }}>Cargando encuesta...</div>
          ) : (
            <>
              <div className="card">
                <div className="question-item">
                  {questions.map((question, idx) => (
                    <div key={idx} className="question-taker">
                      <p className="question-title">
                        {question.title}
                        {question.required && <span style={{ color: '#dc3545' }}> *</span>}
                      </p>

                      {/* Abierta */}
                      {question.type === 'text' && (
                        <div className="form-group">
                          <textarea
                            value={answers[currentPage]?.[idx] || ''}
                            onChange={e => handleAnswerChange(currentPage, { ...answers[currentPage], [idx]: e.target.value })}
                            className="response-input-taker"
                            rows={1}
                          />
                        </div>
                      )}

                      {/* Opción única */}
                      {question.type === 'multiple_choice' && (
                        <div className="choice-builder-container" style={{ marginBottom: 0 }}>
                          {question.options.map((option, optionIndex) => (
                            <div
                              key={optionIndex}
                              className="option-builder"
                              onClick={() => handleAnswerChange(currentPage, { ...answers[currentPage], [idx]: option })}
                              style={{ cursor: 'pointer' }}
                            >
                              <input
                                className="option-radio"
                                type="radio"
                                name={`question-${currentPage}-${idx}`}
                                value={option}
                                checked={answers[currentPage]?.[idx] === option}
                                onChange={() => handleAnswerChange(currentPage, { ...answers[currentPage], [idx]: option })}
                              />
                              <span className="option-edit-input">{option}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Múltiples opciones */}
                      {question.type === 'checkbox' && (
                        <div className="question-options">
                          {question.options.map((option, optionIndex) => {
                            const answerArr = Array.isArray(answers[currentPage]?.[idx]) ? answers[currentPage][idx] : [];
                            const checked = answerArr.includes(option);
                            return (
                              <div
                                key={optionIndex}
                                className={`option-item ${checked ? 'selected' : ''}`}
                                onClick={() => {
                                  const newArr = checked ? answerArr.filter(a => a !== option) : [...answerArr, option];
                                  handleAnswerChange(currentPage, { ...answers[currentPage], [idx]: newArr });
                                }}
                              >
                                <input type="checkbox" value={option} checked={checked} onChange={() => { }} />
                                <span>{option}</span>
                                {checked && <Check size={16} style={{ marginLeft: 'auto', color: '#129859' }} />}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Slider múltiple */}
                      {question.type === 'slider' && Array.isArray(question.sliders) && (
                        <div className="slider-multi-taker">
                          {question.sliders.map((slider, sIdx) => (
                            <div key={sIdx} className="slider-multi-row-taker">
                              {slider.label && <div className="slider-multi-label-taker">{slider.label}</div>}
                              <div className="slider-preview-row-taker">
                                <input
                                  type="range"
                                  min={slider.min}
                                  max={slider.max}
                                  value={answers[currentPage]?.[idx]?.[sIdx] ?? slider.min}
                                  onChange={e => {
                                    const newAnswers = Array.isArray(answers[currentPage]?.[idx]) ? [...answers[currentPage][idx]] : [];
                                    newAnswers[sIdx] = Number(e.target.value);
                                    handleAnswerChange(currentPage, { ...answers[currentPage], [idx]: newAnswers });
                                  }}
                                  className="slider-preview-range-taker"
                                />
                                <span className="slider-value-taker">{answers[currentPage]?.[idx]?.[sIdx] ?? slider.min}</span>
                              </div>
                              <div className="slider-minmax-row-taker">
                                <span className="slider-label-min-taker">{slider.min}</span>
                                <span className="slider-label-max-taker">{slider.max}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Escala */}
                      {question.type === 'scale' && (
                        <div className="scale-taker-row">
                          <p className="scale-label-input">{question.leftLabel || ''}</p>
                          <div className="scale-circles-row">
                            {Array.from({ length: (question.max - question.min + 1) || 11 }, (_, i) => question.min + i).map((val) => (
                              <label key={val} className="scale-circle-label">
                                <span className="scale-number">{val}</span>
                                <input
                                  type="radio"
                                  name={`scale-${currentPage}-${idx}`}
                                  value={val}
                                  checked={answers[currentPage]?.[idx] === val}
                                  onChange={() => handleAnswerChange(currentPage, { ...answers[currentPage], [idx]: val })}
                                  className="scale-radio"
                                />
                              </label>
                            ))}
                          </div>
                          <span className="scale-label-input">{question.rightLabel || ''}</span>
                        </div>
                      )}

                      {/* Matriz */}
                      {question.type === 'matrix' && Array.isArray(question.rows) && Array.isArray(question.columns) && (
                        <div className="matrix-taker-container">
                          <div className="matrix-preview-table-container">
                            <table className="matrix-taker-table">
                              <thead>
                                <tr>
                                  <th className="matrix-corner-taker"></th>
                                  {question.columns.map((col, colIdx) => (
                                    <th className="matrix-col-taker" key={colIdx}>{col}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {question.rows.map((row, rowIdx) => (
                                  <tr key={rowIdx}>
                                    <td className="matrix-row-taker">{row}</td>
                                    {question.columns.map((_, colIdx) => (
                                      <td className="matrix-cell-taker" key={colIdx}>
                                        <input
                                          type="radio"
                                          name={`matrix-${currentPage}-${idx}-row${rowIdx}`}
                                          checked={answers[currentPage]?.[idx]?.[rowIdx] === colIdx}
                                          onChange={() => {
                                            const newAnswers = Array.isArray(answers[currentPage]?.[idx]) ? [...answers[currentPage][idx]] : [];
                                            newAnswers[rowIdx] = colIdx;
                                            handleAnswerChange(currentPage, { ...answers[currentPage], [idx]: newAnswers });
                                          }}
                                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                        />
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Subir archivo */}
                      {question.type === 'file_upload' && (
                        <div
                          className="file-upload-preview-box"
                          onClick={() => fileInputRef.current && fileInputRef.current.click()}
                          onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                          onDrop={async e => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                              const file = e.dataTransfer.files[0];
                              const filePath = await uploadFileTobackend(file);
                              handleAnswerChange(currentPage, { ...answers[currentPage], [idx]: { filePath, name: file.name } });
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="file-upload-icon" style={{ fontSize: 20, color: '#19B394' }}>
                            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M16 36H36C40.4183 36 44 32.4183 44 28C44 23.5817 40.4183 20 36 20C35.4477 20 35 19.5523 35 19C35 14.5817 31.4183 11 24 11C16.5817 11 13 14.5817 13 19C13 19.5523 12.5523 20 12 20C7.58172 20 4 23.5817 4 28C4 32.4183 7.58172 36 12 36Z" stroke="#19B394" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M24 28V18" stroke="#19B394" strokeWidth="2.5" strokeLinecap="round" />
                              <path d="M21 21L24 18L27 21" stroke="#19B394" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                          <div className="file-upload-title" style={{ color: '#19B394' }}>Subir un Archivo</div>
                          <div className="file-upload-subtitle">Arrastre y suelte archivos aquí</div>
                          <input
                            type="file"
                            accept="image/*"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={async e => {
                              const file = e.target.files[0];
                              if (file) {
                                const filePath = await uploadFileTobackend(file);
                                handleAnswerChange(currentPage, { ...answers[currentPage], [idx]: { filePath, name: file.name } });
                              }
                            }}
                          />
                          {answers[currentPage]?.[idx]?.name && (
                            <span style={{ marginTop: 16, color: '#333', fontSize: 15 }}>
                              Archivo seleccionado: {answers[currentPage][idx].name}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Navegación similares a SurveyTaker */}
              <div className="submit-section" style={{ marginTop: 32, display: 'flex', justifyContent: 'center', gap: 16 }}>
                {currentPage > 0 && (
                  <button type="button" className="btn btn-secondary" onClick={() => setCurrentPage(currentPage - 1)}>
                    Anterior página
                  </button>
                )}
                {currentPage < (pages.length - 1) ? (
                  <button type="button" className="btn btn-primary" onClick={() => setCurrentPage(currentPage + 1)}>
                    Siguiente página
                  </button>
                ) : (
                  <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
                    {submitting ? 'Enviando...' : 'Enviar evaluación'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PerformanceSurveyTaker;


